// /orders/new — Handmatig bestelling aanmaken.
// Flow:
//  1. Kies flow (regular / ambassador / sponsor) → bepaalt welke ticket_types je kunt selecteren
//  2. Kies items + qty
//  3. Vul koper-data in
//  4. Vul deelnemer-namen in (auto-prefill = koper)
//  5. Submit-mode kiezen:
//     - "Mollie betaallink mailen" → maakt order pending + mailt pay-link via payment_link template
//     - "Tap betaling op telefoon" → maakt order draft; admin rekent af in Mollie Tap en markeert daarna paid
//     - "Markeer direct betaald (cash/bank)" → maakt order, dan markeer paid + tickets + bevestiging
//     - "Mollie factuur sturen" (sponsor/ambassadeur-flow) → Mollie Sales Invoice issued

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  listTicketTypes,
  createManualOrder,
  markOrderPaid,
  sendPaymentLink,
  sendSponsorInvoice,
  type ManualOrderInput,
} from "@/lib/api";
import { useCurrentEvent } from "@/lib/event-context";
import { fmtEuro } from "@/lib/formatters";

type Flow = "regular" | "ambassador" | "sponsor";
type SubmitMode = "pay_link" | "tap_manual" | "mark_paid" | "sponsor_invoice";

interface Attendee {
  first_name: string;
  last_name: string;
  email: string;
}

export function OrderNew() {
  const navigate = useNavigate();
  const { data: event } = useCurrentEvent();
  const [flow, setFlow] = useState<Flow>("regular");
  const [items, setItems] = useState<Record<string, number>>({});
  const [discount, setDiscount] = useState("");
  const [buyer, setBuyer] = useState({
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    company: "",
    vat_number: "",
    kvk_number: "",
    street_and_number: "",
    postal_code: "",
    city: "",
    country: "NL",
  });
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [notes, setNotes] = useState("");
  const [mode, setMode] = useState<SubmitMode>("pay_link");
  const [submitMessage, setSubmitMessage] = useState("");
  const [result, setResult] = useState<{
    order_id: string;
    order_no: string;
    status: string;
    mode: SubmitMode;
    total_cents?: number;
    payment_url?: string;
    tickets_created?: number;
    invoice_error?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: ticketTypes } = useQuery({
    queryKey: ["ticket_types", event?.id],
    queryFn: () => listTicketTypes(event!.id),
    enabled: !!event?.id,
  });

  // Filter to flow-compatible types: PG checkout_create enforces server-side, dit is alleen UX.
  const eligibleTypes = useMemo(() => {
    if (!ticketTypes) return [];
    if (flow === "regular") return ticketTypes.filter((t) => t.code === "regular");
    if (flow === "sponsor") return ticketTypes.filter((t) => t.flow === "sponsor");
    return ticketTypes.filter((t) => t.code === "ambassador" || t.code === "regular");
  }, [ticketTypes, flow]);

  // Reset items when flow changes
  useEffect(() => {
    setItems({});
  }, [flow]);

  // Adjust submit-mode default for sponsor flow; ambassadeur mag factuur kiezen, regulier niet.
  useEffect(() => {
    if (flow === "sponsor") setMode("sponsor_invoice");
    else if (flow === "regular" && mode === "sponsor_invoice") setMode("pay_link");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow]);

  // Total tickets attendees nodig (qty × includes_tickets per item)
  const ticketsNeeded = useMemo(() => {
    if (!ticketTypes) return 0;
    let n = 0;
    for (const [ttId, qty] of Object.entries(items)) {
      const tt = ticketTypes.find((t) => t.id === ttId);
      if (tt) n += qty * tt.includes_tickets;
    }
    return n;
  }, [items, ticketTypes]);

  const hasSelectedItems = useMemo(() => {
    return Object.values(items).some((qty) => qty > 0);
  }, [items]);
  const sponsorInvoiceReady = mode !== "sponsor_invoice" || (
    !!buyer.company.trim() &&
    (!!buyer.kvk_number.trim() || !!buyer.vat_number.trim()) &&
    !!buyer.street_and_number.trim() &&
    !!buyer.postal_code.trim() &&
    !!buyer.city.trim()
  );

  // Sync attendee-array length aan ticketsNeeded
  useEffect(() => {
    setAttendees((prev) => {
      const next = [...prev];
      while (next.length < ticketsNeeded) {
        next.push({ first_name: "", last_name: "", email: "" });
      }
      return next.slice(0, ticketsNeeded);
    });
  }, [ticketsNeeded]);

  // Subtotaal voor preview (pakt PG niet exact — server-side wint, maar geeft idee)
  const subtotal = useMemo(() => {
    if (!ticketTypes) return 0;
    let s = 0;
    for (const [ttId, qty] of Object.entries(items)) {
      const tt = ticketTypes.find((t) => t.id === ttId);
      if (tt) s += tt.price_cents * qty;
    }
    return s;
  }, [items, ticketTypes]);

  const setItemQty = (ttId: string, qty: number) => {
    setItems((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[ttId];
      else next[ttId] = qty;
      return next;
    });
  };

  const prefillFirstAttendee = () => {
    setAttendees((prev) => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      next[0] = {
        first_name: buyer.first_name,
        last_name: buyer.last_name,
        email: buyer.email,
      };
      return next;
    });
  };

  const submitMut = useMutation({
    mutationFn: async (): Promise<{
      order_id: string;
      order_no: string;
      status: string;
      mode: SubmitMode;
      total_cents?: number;
      payment_url?: string;
      tickets_created?: number;
      invoice_error?: string;
    }> => {
      if (!event?.id) throw new Error("Geen event geladen");
      const cleanItems = Object.entries(items)
        .filter(([, qty]) => qty > 0)
        .map(([ticket_type_id, qty]) => ({ ticket_type_id, qty }));
      if (cleanItems.length === 0) throw new Error("Selecteer minstens één item");
      if (mode === "sponsor_invoice" && flow !== "sponsor" && flow !== "ambassador") {
        throw new Error("Mollie-facturen zijn alleen beschikbaar voor sponsor- en ambassadeurorders.");
      }
      if (mode === "sponsor_invoice" && !sponsorInvoiceReady) {
        throw new Error("Vul bedrijfsnaam, KvK of BTW-nummer en volledig factuuradres in voordat je de Mollie-factuur verstuurt.");
      }

      const input: ManualOrderInput = {
        flow,
        event_id: event.id,
        items: cleanItems,
        buyer: {
          email: buyer.email.trim().toLowerCase(),
          first_name: buyer.first_name || undefined,
          last_name: buyer.last_name || undefined,
          phone: buyer.phone || undefined,
          company: buyer.company || undefined,
          vat_number: buyer.vat_number || undefined,
          kvk_number: buyer.kvk_number || undefined,
          billing_address: mode === "sponsor_invoice" ? {
            street_and_number: buyer.street_and_number || undefined,
            postal_code: buyer.postal_code || undefined,
            city: buyer.city || undefined,
            country: buyer.country || "NL",
          } : undefined,
        },
        attendees: attendees.map((a) => ({
          first_name: a.first_name || undefined,
          last_name: a.last_name || undefined,
          email: a.email || undefined,
        })),
        discount_code: discount || undefined,
        notes: notes || undefined,
        mode: mode === "mark_paid" || mode === "tap_manual" || mode === "sponsor_invoice" ? "skip_mollie" : "mollie",
      };

      const created = await createManualOrder(input);
      const orderId = created.order_id;

      if (mode === "mark_paid") {
        const paid = await markOrderPaid(orderId, notes);
        return {
          order_id: orderId,
          order_no: created.order_no,
          status: paid.status,
          mode,
          total_cents: created.total_cents,
          tickets_created: paid.tickets_created,
        };
      }

      if (mode === "tap_manual") {
        return {
          order_id: orderId,
          order_no: created.order_no,
          status: created.status,
          mode,
          total_cents: created.total_cents,
        };
      }

      if (mode === "sponsor_invoice" && (flow === "sponsor" || flow === "ambassador")) {
        let invoice;
        try {
          invoice = await sendSponsorInvoice(orderId);
        } catch (err) {
          return {
            order_id: orderId,
            order_no: created.order_no,
            status: created.status,
            mode,
            total_cents: created.total_cents,
            invoice_error: err instanceof Error ? err.message : String(err),
          };
        }
        return {
          order_id: orderId,
          order_no: created.order_no,
          status: invoice.mollie_sales_invoice_status === "draft" ? created.status : "invoice_sent",
          mode,
        };
      }

      if (mode === "pay_link" && flow !== "sponsor") {
        try {
          await sendPaymentLink(orderId, submitMessage);
        } catch (err) {
          console.warn("send pay-link failed (order is wel aangemaakt)", err);
        }
        return {
          order_id: orderId,
          order_no: created.order_no,
          status: created.status,
          mode,
          total_cents: created.total_cents,
          payment_url: created.payment_url,
        };
      }

      return {
        order_id: orderId,
        order_no: created.order_no,
        status: created.status,
        mode,
        total_cents: created.total_cents,
        payment_url: created.payment_url,
      };
    },
    onSuccess: (r) => {
      setError(null);
      setResult({
        order_id: r.order_id,
        order_no: r.order_no,
        status: r.status,
        mode: r.mode,
        total_cents: r.total_cents,
        payment_url: r.payment_url,
        tickets_created: r.tickets_created,
        invoice_error: r.invoice_error,
      });
      if (r.invoice_error) setError(`Order is aangemaakt, maar factuur versturen mislukte: ${r.invoice_error}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  const tapPaidMut = useMutation({
    mutationFn: async () => {
      if (!result?.order_id) throw new Error("Geen Tap-order geselecteerd");
      return await markOrderPaid(result.order_id, notes, "tap_manual");
    },
    onSuccess: (paid) => {
      setError(null);
      setResult((prev) => prev ? {
        ...prev,
        status: paid.status,
        tickets_created: paid.tickets_created,
      } : prev);
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!event) return <div className="empty"><span className="spinner" /> Laden…</div>;

  return (
    <>
      <div style={{ marginBottom: "1rem" }}>
        <Link to="/orders" className="pill">← Alle bestellingen</Link>
      </div>

      {result ? (
        <div className="panel">
          <div className="panel-h"><h3>✓ Bestelling aangemaakt — {result.order_no}</h3></div>
          <div style={{ padding: "1rem 0", lineHeight: 1.7 }}>
            <p>Order <code style={{ color: "var(--c-orange)" }}>{result.order_no}</code> staat in het systeem.</p>
            {result.mode === "tap_manual" && result.tickets_created === undefined && (
              <div style={{ marginTop: "1rem", padding: "1rem", border: "1px solid rgba(255,122,26,0.35)", borderRadius: 6, background: "rgba(255,122,26,0.06)" }}>
                <div style={{ fontFamily: "var(--f-display)", color: "var(--c-orange)", textTransform: "uppercase", marginBottom: "0.6rem" }}>
                  Tap betaling afronden
                </div>
                <div style={{ display: "grid", gap: "0.3rem", fontFamily: "var(--f-mono)", fontSize: "0.85rem", marginBottom: "0.9rem" }}>
                  <div>Bedrag in Mollie Tap: <strong style={{ color: "var(--c-orange)" }}>{fmtEuro(result.total_cents ?? 0)}</strong></div>
                  <div>Omschrijving: <code>{result.order_no}</code></div>
                </div>
                <ol style={{ margin: 0, paddingLeft: "1.2rem" }}>
                  <li>Open de Mollie Tap app op deze telefoon.</li>
                  <li>Kies Nieuwe betaling.</li>
                  <li>Vul het bedrag hierboven in en zet de omschrijving op het ordernummer.</li>
                  <li>Laat de klant met pin/contactloos betalen.</li>
                  <li>Kom terug naar deze admin en klik op Betaald via Tap.</li>
                </ol>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "1rem" }}>
                  <a className="btn-admin ghost sm" href="https://www.mollie.com/download/tap-app" target="_blank" rel="noopener">
                    Open Mollie Tap
                  </a>
                  <button
                    className="btn-admin sm"
                    onClick={() => tapPaidMut.mutate()}
                    disabled={tapPaidMut.isPending}
                  >
                    {tapPaidMut.isPending ? "Bezig…" : "✓ Betaald via Tap"}
                  </button>
                </div>
              </div>
            )}
            {result.tickets_created !== undefined && (
              <p>{result.tickets_created} ticket(s) gegenereerd + bevestigingsmail verstuurd.</p>
            )}
            {result.payment_url && (
              <p>
                Betaal-link voor de koper:{" "}
                <a href={result.payment_url} target="_blank" rel="noopener" style={{ color: "var(--c-orange)" }}>
                  {result.payment_url}
                </a>
              </p>
            )}
            {error && (
              <div style={{ background: "rgba(255,80,80,0.12)", border: "1px solid rgba(255,80,80,0.4)", padding: "0.7rem", borderRadius: 6, marginTop: "1rem", fontSize: "0.85rem" }}>
                ⚠ {error}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {result.invoice_error && (
              <button className="btn-admin sm" onClick={() => navigate(`/orders/${result.order_id}`)}>
                Factuurgegevens herstellen →
              </button>
            )}
            <button className="btn-admin sm" onClick={() => navigate(`/orders`)}>
              ← Terug naar overzicht
            </button>
            <button
              className="btn-admin ghost sm"
              onClick={() => {
                setResult(null);
                setItems({});
                setBuyer({
                  email: "",
                  first_name: "",
                  last_name: "",
                  phone: "",
                  company: "",
                  vat_number: "",
                  kvk_number: "",
                  street_and_number: "",
                  postal_code: "",
                  city: "",
                  country: "NL",
                });
                setAttendees([]);
                setDiscount("");
                setNotes("");
              }}
            >
              + Nieuwe bestelling
            </button>
          </div>
        </div>
      ) : (
        <div className="row2">
          <div>
            <div className="panel">
              <div className="panel-h"><h3>1. Type bestelling</h3></div>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                {(["regular", "ambassador", "sponsor"] as Flow[]).map((f) => (
                  <button
                    key={f}
                    className={`tab ${flow === f ? "active" : ""}`}
                    onClick={() => setFlow(f)}
                  >
                    {f === "regular" ? "Regulier" : f === "ambassador" ? "Ambassadeur" : "Sponsor"}
                  </button>
                ))}
              </div>
            </div>

            <div className="panel" style={{ marginTop: "1rem" }}>
              <div className="panel-h"><h3>2. Tickets</h3></div>
              {eligibleTypes.length === 0 && <div className="empty">Geen ticket-types beschikbaar voor deze flow.</div>}
              {eligibleTypes.map((tt) => (
                <div key={tt.id} style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "1rem", alignItems: "center", padding: "0.7rem 0", borderBottom: "1px dashed rgba(255,122,26,0.2)" }}>
                  <div>
                    <strong>{tt.name}</strong>
                    <div style={{ fontSize: "0.78rem", opacity: 0.7 }}>{tt.description}</div>
                    <div style={{ fontFamily: "var(--f-mono)", fontSize: "0.72rem", opacity: 0.6 }}>code: {tt.code} · {tt.includes_tickets} ticket(s) p/stuk</div>
                  </div>
                  <div style={{ color: "var(--c-orange)", fontFamily: "var(--f-display)" }}>{fmtEuro(tt.price_cents)}</div>
                  <input
                    type="number"
                    min={0}
                    max={tt.max_per_order ?? 999}
                    value={items[tt.id] ?? 0}
                    onChange={(e) => setItemQty(tt.id, Math.max(0, Number(e.target.value)))}
                    style={{ width: 80 }}
                  />
                </div>
              ))}
              <div style={{ marginTop: "1rem", textAlign: "right", fontFamily: "var(--f-mono)" }}>
                Subtotaal: <strong style={{ color: "var(--c-orange)" }}>{fmtEuro(subtotal)}</strong>
                <div style={{ fontSize: "0.72rem", opacity: 0.6 }}>(server berekent definitief — incl. groeps­korting / ambassadeur-discount)</div>
              </div>
            </div>

            {flow !== "sponsor" && (
              <div className="panel" style={{ marginTop: "1rem" }}>
                <div className="panel-h"><h3>Optioneel: kortingscode</h3></div>
                <div className="field">
                  <label>Code</label>
                  <input
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value.toUpperCase())}
                    placeholder="bv. AMB-A1B2C3"
                  />
                </div>
              </div>
            )}

            <div className="panel" style={{ marginTop: "1rem" }}>
              <div className="panel-h"><h3>3. Koper</h3></div>
              <div className="form-grid">
                <div className="field"><label>Email *</label>
                  <input type="email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} required />
                </div>
                <div className="field"><label>Telefoon</label>
                  <input value={buyer.phone} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} />
                </div>
                <div className="field"><label>Voornaam</label>
                  <input value={buyer.first_name} onChange={(e) => setBuyer({ ...buyer, first_name: e.target.value })} />
                </div>
                <div className="field"><label>Achternaam</label>
                  <input value={buyer.last_name} onChange={(e) => setBuyer({ ...buyer, last_name: e.target.value })} />
                </div>
              </div>
              {mode === "sponsor_invoice" && (
                <div className="form-grid" style={{ marginTop: "0.5rem" }}>
                  <div className="field"><label>Bedrijfsnaam *</label>
                    <input value={buyer.company} onChange={(e) => setBuyer({ ...buyer, company: e.target.value })} />
                  </div>
                  <div className="field"><label>KvK *</label>
                    <input value={buyer.kvk_number} onChange={(e) => setBuyer({ ...buyer, kvk_number: e.target.value })} />
                  </div>
                  <div className="field"><label>BTW-nr (of KvK)</label>
                    <input value={buyer.vat_number} onChange={(e) => setBuyer({ ...buyer, vat_number: e.target.value })} />
                  </div>
                  <div className="field"><label>Straat + huisnummer *</label>
                    <input value={buyer.street_and_number} onChange={(e) => setBuyer({ ...buyer, street_and_number: e.target.value })} />
                  </div>
                  <div className="field"><label>Postcode *</label>
                    <input value={buyer.postal_code} onChange={(e) => setBuyer({ ...buyer, postal_code: e.target.value })} />
                  </div>
                  <div className="field"><label>Plaats *</label>
                    <input value={buyer.city} onChange={(e) => setBuyer({ ...buyer, city: e.target.value })} />
                  </div>
                  <div className="field"><label>Land</label>
                    <input value={buyer.country} onChange={(e) => setBuyer({ ...buyer, country: e.target.value.toUpperCase() })} />
                  </div>
                </div>
              )}
            </div>

            {ticketsNeeded > 0 && (
              <div className="panel" style={{ marginTop: "1rem" }}>
                <div className="panel-h">
                  <h3>4. Deelnemers ({ticketsNeeded})</h3>
                  <button type="button" className="pill" onClick={prefillFirstAttendee}>
                    Eerste = koper
                  </button>
                </div>
                {attendees.map((a, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem", marginBottom: "0.5rem" }}>
                    <input
                      placeholder={`Voornaam #${i + 1}`}
                      value={a.first_name}
                      onChange={(e) => {
                        const next = [...attendees]; next[i] = { ...a, first_name: e.target.value }; setAttendees(next);
                      }}
                    />
                    <input
                      placeholder="Achternaam"
                      value={a.last_name}
                      onChange={(e) => {
                        const next = [...attendees]; next[i] = { ...a, last_name: e.target.value }; setAttendees(next);
                      }}
                    />
                    <input
                      placeholder="Email"
                      value={a.email}
                      onChange={(e) => {
                        const next = [...attendees]; next[i] = { ...a, email: e.target.value }; setAttendees(next);
                      }}
                    />
                  </div>
                ))}
                <div style={{ fontSize: "0.72rem", opacity: 0.6 }}>Tickets staan op naam van deze deelnemers. QR-code is bij betaling automatisch gemaakt en gemaild naar de <em>koper</em>.</div>
              </div>
            )}
          </div>

          <div>
            <div className="panel" style={{ position: "sticky", top: "1rem" }}>
              <div className="panel-h"><h3>5. Verwerken</h3></div>

              <div className="field">
                <label>Verwerkings-modus</label>
                {flow === "sponsor" ? (
                  <select value={mode} onChange={(e) => setMode(e.target.value as SubmitMode)}>
                    <option value="sponsor_invoice">Mollie factuur aanmaken + sturen</option>
                    <option value="mark_paid">Markeer direct als betaald</option>
                  </select>
                ) : (
                  <select value={mode} onChange={(e) => setMode(e.target.value as SubmitMode)}>
                    <option value="pay_link">Mollie betaallink mailen</option>
                    {flow === "ambassador" && <option value="sponsor_invoice">Mollie zakelijke factuur aanmaken + sturen</option>}
                    <option value="tap_manual">Tap betaling op telefoon</option>
                    <option value="mark_paid">Markeer direct als betaald (cash/bank)</option>
                  </select>
                )}
              </div>

              {mode === "pay_link" && (
                <div className="field">
                  <label>Bericht in mail (optioneel)</label>
                  <textarea
                    rows={3}
                    placeholder="Bijv. 'Hoi Henk, hier de link zoals besproken'"
                    value={submitMessage}
                    onChange={(e) => setSubmitMessage(e.target.value)}
                  />
                </div>
              )}

              <div className="field">
                <label>Interne notitie (optioneel)</label>
                <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>

              {error && (
                <div style={{ background: "rgba(255,80,80,0.12)", border: "1px solid rgba(255,80,80,0.4)", padding: "0.7rem", borderRadius: 6, marginBottom: "0.7rem", fontSize: "0.85rem" }}>
                  ⚠ {error}
                </div>
              )}

              <button
                className="btn-admin"
                disabled={submitMut.isPending || !buyer.email || !hasSelectedItems || !sponsorInvoiceReady}
                onClick={() => submitMut.mutate()}
                style={{ width: "100%" }}
              >
                {submitMut.isPending ? "Bezig…" : "→ Bestelling aanmaken"}
              </button>

              <div style={{ marginTop: "1rem", fontSize: "0.72rem", opacity: 0.6, lineHeight: 1.6 }}>
                <strong>Wat gebeurt er:</strong>
                <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                  <li>Order wordt aangemaakt met source <code>admin</code></li>
                  {mode === "pay_link" && <li>Mollie payment + email met betaallink → koper</li>}
                  {mode === "tap_manual" && <li>Order wordt klaargezet; reken af in Mollie Tap en klik daarna Betaald via Tap</li>}
                  {mode === "mark_paid" && <li>Status → paid + tickets gegenereerd + bevestigingsmail verstuurd</li>}
                  {mode === "sponsor_invoice" && <li>Mollie maakt en verstuurt de zakelijke factuur; status → invoice_sent</li>}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
